import PolicyInformation from "../common/policy-data-page/policy-information";
import PolicySchedule from "../common/policy-data-page/policy-schedule";
import PolicySpecialUnderwritingProvision from "../common/policy-data-page/policy-special-underwriting-provision";
import PolicyTurnAroundTime from "../common/policy-data-page/policy-turn-around-time";
import { usePolicyData } from "../../../../hooks/populate-data/use-policy-data";
import LoadingState from "../../../ui/loadibg-state";
import ErrorState from "../../../ui/error-state";
import PolicySigningAddress from "../common/policy-data-page/policy-signing-address";
import { useEffect, useMemo, useRef, useState } from "react";
import HeaderModal from "../../../ui/header-modal";
import { useDispatch } from "react-redux";
import { resetPolicyTATState } from "../../../../features/ebam/plicy-data-page/policy-tat/policy-tat.slice";
import { CheckSVG, EditSVG, PolicyInformationIconSVG, PolicyScheduleIconSVG, SigningAddressIconSVG, TurnAroundTimeIconSVG, UnderwritingIconSVG } from "../../../../assets/icon/svg";
import { usePolicyActions } from "../../../../hooks/actions/use-policy-actions";
import { useProposalData } from "../../../../hooks/populate-data/use-proposal-data";
import { useProposalTypeData } from "../../../../hooks/proposal/use-proposal-type-data";
import { usePolicyDataState } from "../../../../hooks/policy/use-policy-data-state";

type PolicyTab =
    | "information"
    | "schedule"
    | "underwriting"
    | "turnAroundTime"
    | "signingAddress";

type TabIconProps = {
    className?: string;
};

export default function PolicyDataPageModal() {

    const dispatch = useDispatch()
    const { isGTLIPolicy } = useProposalTypeData()
    const { policyIsEditingTAT, isPIEligibleIndividuals } = usePolicyDataState()
    const {
        isLoadingPolicy,
        isErrorPolicy,
        isLoadingPolicyTAT,
        isErrorPolicyTAT,
        tatItemsApi,
        populatePolicyInformation,
        repopulateAll: repopulateAllPolicy,
    } = usePolicyData();

    const { 
        proposal, 
        isLoadingProposal, 
        repopulateAll, 
        repopulatePolicy,
    } = useProposalData();
    const { 
        handleEditPolicyTAT,
        isLoadingUpdateTAT,
        onClosePolicyModal,
        handleEditTAT,
    } = usePolicyActions();

    const populatedProposalRef = useRef<string | null>(null);
    
    const proposalSignature = useMemo(
        () => (proposal ? JSON.stringify(proposal) : null),
        [proposal],
    );

    useEffect(() => {
        if (!proposal?.application_id || !proposalSignature) {
            return;
        }

        if (populatedProposalRef.current === proposalSignature) {
            return;
        }

        populatedProposalRef.current = proposalSignature;
        repopulatePolicy();
        repopulateAll();
    }, [ proposal?.application_id, proposalSignature, repopulateAll, ]);

    // const populatedProposalIdRef = useRef<number | null>(null);
    // useEffect(() => {
    //     if (!proposal?.application_id) {
    //         return;
    //     }

    //     if (isPIEligibleIndividuals) {
    //         return;
    //     }

    //     populatedProposalIdRef.current = proposal.application_id;
    //     repopulateAllPolicy();
    // }, [
    //     proposal?.application_id,
    //     repopulateAllPolicy,
    // ]);
    

    const isLoading = isLoadingPolicy || isLoadingPolicyTAT || isLoadingProposal;
    const isError = isErrorPolicy || isErrorPolicyTAT;

    const [activeTab, setActiveTab] = useState<PolicyTab>("information");
    const tabs: { id: PolicyTab; label: string; icon: React.ComponentType<TabIconProps> }[] = [
        {
            id: "information",
            label: "Policy Information",
            icon: PolicyInformationIconSVG,
        },
        {
            id: "schedule",
            label: "Policy Schedule",
            icon: PolicyScheduleIconSVG,
        },
        {
            id: "underwriting",
            label: "Underwriting Provisions",
            icon: UnderwritingIconSVG,
        },
        {
            id: "turnAroundTime",
            label: "Turn-Around Time",
            icon: TurnAroundTimeIconSVG,
        },
        {
            id: "signingAddress",
            label: "Address and Doc. code",
            icon: SigningAddressIconSVG,
        },
    ];

    const renderActiveTab = () => {
        switch (activeTab) {
            case "information":
                return <PolicyInformation />;
            case "schedule":
                return <PolicySchedule />;
            case "underwriting":
                return <PolicySpecialUnderwritingProvision />;
            case "turnAroundTime":
                return <PolicyTurnAroundTime />;
            case "signingAddress":
                return <PolicySigningAddress />;
            default:
                return <PolicyInformation />;
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-start z-50 p-4">
            <div className="h-[96dvh] w-full bg-[#f5f9fd] rounded-xl overflow-hidden shadow my-auto flex flex-col sm:h-[90dvh] sm:max-w-[95vw] lg:max-w-[700px] xl:max-w-[1000px] 2xl:max-w-[1100px]">
                {/* Header */}
                <HeaderModal
                    close={onClosePolicyModal}
                    title="Policy Data Page"
                    subtitle="Provide the necessary details below to generate the Policy Data Page."
                />
                {isLoading ? (
                    <div className="h-[70dvh]">
                        <LoadingState />
                    </div>
                ) : isError ? (
                    <div className="h-[70dvh]">
                        <ErrorState />
                    </div>
                ) : (
                    <>
                        <div className="border-b border-gray-200 bg-white">
                            <div className="overflow-x-auto">
                                <div className="flex min-w-max px-2 sm:px-4">
                                    {tabs.filter((tab) => !(isGTLIPolicy && tab.id === "underwriting"))
                                    .map((tab) => {
                                        const isActive = activeTab === tab.id;
                                        const Icon = tab.icon;
                                        return (
                                            <button
                                                key={tab.id}
                                                type="button"
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`relative flex items-center gap-1.5 whitespace-nowrap px-3 py-3 text-xs font-medium transition-colors sm:gap-2 sm:px-4 ${
                                                    isActive ? "text-[#186cb2]" : "text-gray-500 hover:text-gray-700"
                                                }`}
                                            >
                                                <Icon className="h-4 w-4 shrink-0" />
                                                <span>{tab.label}</span>
                                                {isActive && (
                                                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[#186cb2]" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        {/* Content */}
                        <div className="flex-1 overflow-y-auto bg-[#FAFAFA] p-2 sm:p-4 md:p-5 lg:p-6">
                            {renderActiveTab()}
                        </div>
                        {/* <PolicyFooter/> */}
                        {activeTab === "turnAroundTime" && (
                            <div className="flex flex-shrink-0 flex-col gap-2 border-t border-gray-200 bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-end sm:px-4 sm:py-4 md:px-6">
                                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
                                    {!policyIsEditingTAT ? (
                                        <button
                                            type="button"
                                            onClick={() => handleEditTAT(tatItemsApi)}
                                            className="flex h-8 items-center justify-center gap-2 rounded border px-4 text-xs font-medium  transition border-[#186cb2] bg-[#eef6fc] text-[#186cb2]"
                                        >
                                            <EditSVG className="h-3 w-3" />
                                            Edit
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => dispatch(resetPolicyTATState())}
                                                className="h-8 rounded border border-gray-300 bg-white px-4 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleEditPolicyTAT}
                                                className="flex h-8 items-center gap-2 rounded bg-[#186cb2] px-4 text-xs font-medium text-white transition hover:bg-[#145a96]"
                                            >
                                                {isLoadingUpdateTAT ? (
                                                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                                ) : (
                                                    <CheckSVG className="h-3 w-3" />
                                                )}
                                                {isLoadingUpdateTAT ? "Saving..." : "Save"}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}